import { Link, useLoaderData } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen,
  Clock,
  Star,
  Tag,
  Users,
  Award,
  Laptop,
  ArrowRight,
} from "lucide-react";
import { AuthlessHomepageLoaderData } from "@/routes/homePage/types/authlessHomePageDTO";
import { CourseDto } from "@/routes/courses/types/coursesDTO";
import { ConstantHelper } from "@/services/helpers/constantHelper";
import { TimeHelper } from "@/services/helpers/timeHelpers";
import { sanitizeHtml } from "@/lib/sanitize";

function FeaturedCourseCard({ course }: { course: CourseDto }) {
  return (
    <Link to={`/course/${course.id}`} className="flex">
      <Card className="w-full overflow-hidden hover:shadow-md transition-shadow border border-gray-200 flex flex-col">
        <div className="aspect-video bg-gray-100 flex items-center justify-center flex-shrink-0">
          {course.image ? (
            <img
              src={course.image}
              alt={course.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <BookOpen className="h-10 w-10 text-gray-300" />
          )}
        </div>
        <CardContent className="p-4 flex flex-col flex-1">
          <Badge
            className={`${ConstantHelper.getLevelColor(course.level)} self-start mb-2`}
          >
            {ConstantHelper.getLevelText(course.level)}
          </Badge>
          <h3 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2">
            {course.name}
          </h3>
          <p
            className="text-xs text-gray-500 mb-3 line-clamp-3 overflow-hidden flex-1"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(course.description ?? ""),
            }}
          />
          <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {TimeHelper.convertMinuteToText(course.durationMinute)}
            </span>
            <span className="flex items-center gap-1">
              <BookOpen className="h-3 w-3" />
              {course.totalLessons} ders
            </span>
          </div>
          <div className="flex items-center justify-between mt-auto">
            <span className="text-base font-bold text-gray-900">
              {course.priceText}
            </span>
            <span className="text-xs text-primary font-medium flex items-center gap-1">
              Kursa git <ArrowRight className="h-3 w-3" />
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function DiscountedCourseCard({ course }: { course: CourseDto }) {
  const discountPercent = Math.round(
    ((course.price - course.discountedPrice) / course.price) * 100,
  );
  return (
    <Link to={`/course/${course.id}`} className="flex">
      <Card className="w-full overflow-hidden hover:shadow-md transition-shadow border-2 border-orange-200 bg-orange-50 flex flex-col">
        <div className="aspect-video bg-orange-100 flex items-center justify-center flex-shrink-0 relative">
          {course.image ? (
            <img
              src={course.image}
              alt={course.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <Tag className="h-10 w-10 text-orange-300" />
          )}
          <Badge className="absolute top-2 right-2 bg-orange-500 text-white text-xs border-0">
            %{discountPercent} indirim
          </Badge>
        </div>
        <CardContent className="p-4 flex flex-col flex-1">
          <div className="flex items-center gap-1 mb-2">
            <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400" />
            <span className="text-xs font-medium text-gray-700">
              {course.rating.toFixed(1)}
            </span>
            <span className="text-xs text-gray-400">
              ({course.totalRatings})
            </span>
          </div>
          <h3 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2">
            {course.name}
          </h3>
          <p
            className="text-xs text-gray-500 mb-3 line-clamp-3 overflow-hidden flex-1"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(course.description ?? ""),
            }}
          />
          <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {TimeHelper.convertMinuteToText(course.durationMinute)}
            </span>
            <span className="flex items-center gap-1">
              <BookOpen className="h-3 w-3" />
              {course.totalLessons} ders
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-auto">
            <span className="text-lg font-bold text-orange-500">
              {course.discountedPriceText}
            </span>
            <span className="text-sm line-through text-gray-400">
              {course.priceText}
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function AuthlessHomepage() {
  const data = useLoaderData() as AuthlessHomepageLoaderData;

  return (
    <main className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 pb-6 sm:pb-10 space-y-8">
      {/* Hero / About Codeline section */}
      <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8 sm:p-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Kariyerini Bir Üst Seviyeye Taşı
            </h2>
            <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto">
              Codeline ile alanında uzman eğitmenlerden öğren, sertifika kazan
              ve iş hayatında fark yarat.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">
                Uzman Eğitmenler
              </h3>
              <p className="text-sm text-gray-500">
                Sektör deneyimine sahip profesyonellerden öğren
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Laptop className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">
                Kendi Hızında İlerle
              </h3>
              <p className="text-sm text-gray-500">
                İstediğin zaman, istediğin yerden erişim
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Award className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">
                Sertifika Kazan
              </h3>
              <p className="text-sm text-gray-500">
                Tamamladığın kurslar için resmi sertifika al
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                <BookOpen className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">
                Ömür Boyu Erişim
              </h3>
              <p className="text-sm text-gray-500">
                Satın aldığın kurslara sonsuza kadar ulaş
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/courses">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary-600 text-white w-full sm:w-auto"
              >
                Kursları Keşfet
              </Button>
            </Link>
            <Link to="/register">
              <Button
                size="lg"
                variant="outline"
                className="border-primary text-primary hover:bg-primary/5 bg-transparent w-full sm:w-auto"
              >
                Ücretsiz Üye Ol
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Courses section: flat 4-col grid so all cards are equal width → equal image height */}
      <div>
        {/* Section headers */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
          <div className="sm:col-span-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Öne Çıkan Kurslar
            </h2>
            <Link to="/courses">
              <Button variant="ghost" size="sm" className="text-primary text-xs">
                Tümünü gör
              </Button>
            </Link>
          </div>
          <div className="sm:col-span-1 flex items-center gap-2">
            <Tag className="h-4 w-4 text-orange-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              İndirimli Kurslar
            </h2>
          </div>
        </div>

        {/* All 4 cards in same grid → identical column width → identical aspect-video height */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
          {data.featuredCourses.map((course) => (
            <FeaturedCourseCard key={course.id} course={course} />
          ))}
          {data.discountedCourses.length > 0 ? (
            data.discountedCourses.map((course) => (
              <DiscountedCourseCard key={course.id} course={course} />
            ))
          ) : (
            <Card className="flex items-center justify-center border border-dashed border-gray-300">
              <p className="text-sm text-gray-400 p-4 text-center">
                Şu an aktif indirim bulunmuyor
              </p>
            </Card>
          )}
        </div>
      </div>
    </main>
  );
}
